import { Link, useParams } from "react-router-dom";
import HeatMeter from "@/components/base/HeatMeter";
import Seo from "@/components/feature/Seo";
import ScrollToTop from "@/components/feature/ScrollToTop";
import SiteFooter from "@/components/feature/SiteFooter";
import SiteHeader from "@/components/feature/SiteHeader";
import TagBadge from "@/components/base/TagBadge";
import { getTopicById, hotTopics } from "@/mocks/topics";

const formatNumber = (value: number) =>
  value >= 10000 ? `${(value / 10000).toFixed(1)}万` : String(value);

export default function TopicPage() {
  const { id } = useParams<{ id: string }>();
  const topic = id ? getTopicById(id) : undefined;

  if (!topic) {
    return (
      <>
        <Seo
          title="话题不存在_爆点观察局"
          description="该话题记录不存在或已归档，请返回热搜榜查看当前正在扩散的话题。"
          keywords="话题不存在,热搜榜,爆点观察局"
          path="/topic"
        />
        <SiteHeader />
        <main className="flex min-h-[60vh] items-center justify-center bg-background-50 px-4 pt-24">
          <div className="text-center">
            <i className="ri-ghost-line text-4xl text-foreground-400" />
            <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">
              这条话题记录已经归档
            </h1>
            <p className="mt-3 text-sm text-foreground-600">
              复盘记录通常保留 7 天，你可以回到热搜榜看看现在正在被传疯的话题。
            </p>
            <Link
              to="/ranking"
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              返回热搜榜
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </main>
        <SiteFooter />
      </>
    );
  }

  const related = hotTopics
    .filter((item) => item.id !== topic.id)
    .sort((a, b) => {
      const sameA = a.categoryId === topic.categoryId ? 1 : 0;
      const sameB = b.categoryId === topic.categoryId ? 1 : 0;
      if (sameA !== sameB) return sameB - sameA;
      return b.heat - a.heat;
    })
    .slice(0, 4);

  const maxHeat = hotTopics[0].heat;
  const peakIndex = topic.timeline.reduce(
    (best, point, index, arr) => (point.heat > arr[best].heat ? index : best),
    0,
  );
  const riseHours = topic.timeline[peakIndex]?.time.replace("T+", "").replace("h", "") || "—";

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: topic.title,
      description: topic.summary,
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "/" },
        { "@type": "ListItem", position: 2, name: "热搜榜", item: "/ranking" },
        { "@type": "ListItem", position: 3, name: topic.title, item: `/topic/${topic.id}` },
      ],
    },
  ];

  return (
    <>
      <Seo
        title={`${topic.title}_话题扩散轨迹_爆点观察局`}
        description={`${topic.summary} 综合热度 ${formatNumber(topic.heat)}，累计转发 ${formatNumber(
          topic.shares,
        )}，峰值出现在${topic.peakTime}。查看完整扩散时间线与传播分析。`}
        keywords={`${topic.category},黑料网爆话题,话题转发,扩散轨迹,热搜复盘`}
        path={`/topic/${topic.id}`}
        jsonLd={jsonLd}
      />
      <SiteHeader />
      <main>
        <section className="bg-foreground-950 pb-12 pt-28 md:pb-14 md:pt-32">
          <div className="mx-auto w-full max-w-shell px-4 md:px-8">
            <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-2 text-xs text-background-400">
              <Link to="/" className="transition-colors hover:text-accent-400">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <Link to="/ranking" className="transition-colors hover:text-accent-400">
                热搜榜
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="line-clamp-1 text-background-100">{topic.category}</span>
            </nav>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              <TagBadge tag={topic.tag} />
              <span className="rounded-full border border-background-50/20 px-2.5 py-0.5 text-xs text-background-200">
                {topic.category}
              </span>
              <span className="flex items-center gap-1 text-xs text-background-300">
                <span className="live-dot h-1.5 w-1.5 rounded-full bg-accent-500" />
                扩散进行中
              </span>
            </div>

            <h1 className="mt-4 max-w-4xl font-heading text-2xl font-black leading-tight text-background-50 md:text-4xl">
              {topic.title}
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-background-300 md:text-base">
              {topic.summary}
            </p>

            <dl className="mt-8 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-background-50/15 bg-background-50/10 md:grid-cols-4">
              {[
                { label: "综合热度", value: formatNumber(topic.heat) },
                { label: "累计转发", value: formatNumber(topic.shares) },
                { label: "扩散耗时", value: `${riseHours} 小时` },
                { label: "峰值时刻", value: topic.peakTime },
              ].map((item) => (
                <div key={item.label} className="bg-foreground-950/60 px-4 py-5 backdrop-blur-sm md:px-6">
                  <dt className="text-xs text-background-400">{item.label}</dt>
                  <dd className="mt-2 font-heading text-lg font-bold text-background-50 md:text-xl">
                    {item.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        <section className="bg-background-50 py-12 md:py-16">
          <div className="mx-auto w-full max-w-shell px-4 md:px-8">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
              <div className="lg:col-span-8">
                <article className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-8">
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    话题扩散时间线
                  </h2>
                  <p className="mt-2 text-sm text-foreground-600">
                    从素材出现到热度回落，下面是这条话题被传开的完整节点记录。
                  </p>

                  <ol className="mt-7 space-y-6">
                    {topic.timeline.map((point, index) => {
                      const isPeak = index === peakIndex;
                      return (
                        <li key={point.time} className="relative pl-8">
                          <span
                            className={`absolute left-0 top-1 flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                              isPeak
                                ? "border-primary-500 bg-primary-500"
                                : "border-background-300 bg-background-50"
                            }`}
                          >
                            {isPeak && <i className="ri-fire-fill text-[9px] text-background-50" />}
                          </span>
                          {index !== topic.timeline.length - 1 && (
                            <span className="absolute left-[7px] top-6 h-full w-px bg-background-300" />
                          )}
                          <div className="flex flex-wrap items-center gap-3">
                            <span className="rounded-md bg-foreground-950 px-2.5 py-1 font-heading text-xs font-bold text-background-50">
                              {point.time}
                            </span>
                            <h3 className="font-heading text-base font-bold text-foreground-950">
                              {point.label}
                            </h3>
                            {isPeak && (
                              <span className="rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-800">
                                热度峰值
                              </span>
                            )}
                          </div>
                          <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                            {point.desc}
                          </p>
                          <div className="mt-3 flex items-center gap-3">
                            <HeatMeter value={point.heat} max={maxHeat} className="max-w-xs" />
                            <span className="whitespace-nowrap text-xs text-foreground-400">
                              热度 {formatNumber(point.heat)}
                            </span>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                </article>

                <section className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5 md:p-8">
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    传播分析
                  </h2>
                  <div className="mt-4 space-y-4 text-sm leading-relaxed text-foreground-700">
                    <p>
                      这条话题的扩散路径属于
                      <strong className="font-medium text-foreground-950">
                        {topic.scope.length >= 4 ? "多平台同步型" : "单平台发散型"}
                      </strong>
                      。素材最早出现后，先在{topic.scope[0]}形成了第一轮讨论，随后向
                      {topic.scope.slice(1).join("、") || "其他平台"}迁移。
                    </p>
                    <p>
                      从时间线看，热度在
                      <strong className="font-medium text-foreground-950">{topic.peakTime}</strong>
                      达到峰值。这个位置通常对应两类事件：一是出现了新的信息增量，二是二创内容集中发布。本条话题更接近后者。
                    </p>
                    <p className="rounded-md border-l-2 border-accent-500 bg-accent-50 px-4 py-3 text-accent-900">
                      {topic.highlight}
                    </p>
                    <p>
                      需要提醒的是，热度值反映的是被谈论的规模，并不代表信息本身已经被证实。在转发量快速上升的阶段，素材往往会被反复简化，建议以素材层级而非转发量来判断可信度。
                    </p>
                  </div>
                </section>
              </div>

              <aside className="lg:col-span-4">
                <div className="sticky top-24 space-y-6">
                  <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                    <h2 className="font-heading text-base font-bold text-foreground-950">
                      已扩散平台
                    </h2>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {topic.scope.map((item) => (
                        <span
                          key={item}
                          className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                    <h2 className="font-heading text-base font-bold text-foreground-950">
                      相关话题推荐
                    </h2>
                    <ul className="mt-3 divide-y divide-background-200">
                      {related.map((item) => (
                        <li key={item.id} className="py-3 first:pt-0 last:pb-0">
                          <Link
                            to={`/topic/${item.id}`}
                            className="group flex items-start gap-2.5"
                          >
                            <TagBadge tag={item.tag} className="mt-0.5 shrink-0" />
                            <span>
                              <span className="block text-sm leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
                                {item.title}
                              </span>
                              <span className="mt-1 block text-xs text-foreground-400">
                                热度 {formatNumber(item.heat)}
                              </span>
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="rounded-lg border border-background-200 bg-foreground-950 p-5">
                    <p className="flex items-center gap-2 font-heading text-sm font-bold text-background-50">
                      <i className="ri-information-line text-accent-500" />
                      关于数据口径
                    </p>
                    <p className="mt-2 text-xs leading-relaxed text-background-300">
                      热度值由搜索、讨论、转发、二创等公开可见维度加权合成，用于横向比较话题受关注程度，不代表事件真伪。
                    </p>
                    <Link
                      to="/ranking"
                      className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-accent-400 transition-colors hover:text-accent-300"
                    >
                      返回完整榜单
                      <i className="ri-arrow-right-line" />
                    </Link>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <ScrollToTop />
    </>
  );
}